import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  emptyWebPublicExperienceV1,
  type WebPublicExperienceV1,
} from '../types/webPublicExperience';

export type WebBookingBranding = {
  tenantName: string | null;
  logoUrl: string | null;
  webPublicExperience: WebPublicExperienceV1;
  profileSlug: string | null;
};

type WebBookingBrandingContextValue = WebBookingBranding & {
  setBranding: (b: WebBookingBranding) => void;
};

const WebBookingBrandingContext = createContext<WebBookingBrandingContextValue | null>(
  null,
);

export function WebBookingBrandingProvider({ children }: { children: ReactNode }) {
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [webPublicExperience, setWebPublicExperience] =
    useState<WebPublicExperienceV1>(emptyWebPublicExperienceV1);
  const [profileSlug, setProfileSlug] = useState<string | null>(null);

  const setBranding = useCallback((b: WebBookingBranding) => {
    setTenantName(b.tenantName);
    setLogoUrl(b.logoUrl);
    setWebPublicExperience(b.webPublicExperience ?? emptyWebPublicExperienceV1());
    setProfileSlug(b.profileSlug ?? null);
  }, []);

  const value = useMemo(
    () => ({
      tenantName,
      logoUrl,
      webPublicExperience,
      profileSlug,
      setBranding,
    }),
    [tenantName, logoUrl, webPublicExperience, profileSlug, setBranding],
  );

  return (
    <WebBookingBrandingContext.Provider value={value}>
      {children}
    </WebBookingBrandingContext.Provider>
  );
}

export function useWebBookingBranding(): WebBookingBrandingContextValue {
  const ctx = useContext(WebBookingBrandingContext);
  if (!ctx) {
    throw new Error('useWebBookingBranding must be used within WebBookingBrandingProvider');
  }
  return ctx;
}
